import { useData } from "data/data-provider";
import { isUsableUrl } from "utils/isUsableUrl";

const WhyChooseUS = () => {
  const { whyUs } = useData();

  // 1NOW layout variant chosen in the FMS (Section.layout_variant):
  //   "card_grid" → current Design A 2-col card grid (default visual)
  //   "icon_list" → vertical list with icon-left, copy-right per row
  // Default to "card_grid" so tenants who haven't picked a layout get the
  // exact same render as before the picker existed.
  const layoutVariant: "card_grid" | "icon_list" =
    whyUs?.layout_variant === "icon_list" ? "icon_list" : "card_grid";

  // Icon list layout — single column, each row is an icon on the left +
  // heading & description on the right. Reuses Design A's own typography
  // (text-2xl, text-text-color / text-secondary-text-color) and icon
  // treatment (max-w-14 max-h-14 rounded-md) so it matches Design A.
  if (layoutVariant === "icon_list") {
    return (
      <div className="py-12">
        {(whyUs?.heading || whyUs?.description) && (
          <div className="mb-8">
            <h2 className="leading-none text-2xl text-text-color font-semibold mb-4 text-center sm:text-start">
              <span
                className="text-text-color"
                dangerouslySetInnerHTML={{ __html: whyUs?.heading }}
              />
            </h2>
            <p className="text-secondary-text-color text-2xl text-center sm:text-start">
              <span
                className="text-secondary-text-color"
                dangerouslySetInnerHTML={{ __html: whyUs?.description }}
              />
            </p>
          </div>
        )}

        <div className="flex flex-col gap-8">
          {whyUs?.keypoints?.map((feature: any, index: any) => (
            <div key={index} className="flex items-start gap-6 text-left">
              {isUsableUrl(feature?.image_url) && (
                <img
                  className="text-blue-500 text-4xl max-w-14 max-h-14 rounded-md shrink-0"
                  src={feature.image_url}
                  alt={feature.image_url}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
              <div className="flex flex-col">
                <span
                  className="leading-none text-text-color text-2xl font-semibold mb-1"
                  dangerouslySetInnerHTML={{ __html: feature.heading }}
                />
                <p
                  className="text-secondary-text-color text-2xl leading-none"
                  dangerouslySetInnerHTML={{ __html: feature.description }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Card grid layout (default) — section header on top, then each keypoint in
  // its own bordered card laid out in a responsive multi-column grid (1 col
  // mobile, 2 col sm, 3 col lg). This is what the FMS "Card grid" picker
  // thumbnail promises (boxed cards) and is what makes the variant read
  // distinctly from the full-width stacked Icon list above. Uses Design A's
  // own card idiom (rounded + border-secondary-100 + bg-card-color + hover
  // shadow, same as FleetCard) so it matches the rest of the design.
  return (
    <div className="py-12">
      {(whyUs?.heading || whyUs?.description) && (
        <div className="mb-8">
          <h2 className="leading-none text-2xl text-text-color font-semibold mb-4 text-center sm:text-start">
            <span
              className="text-text-color"
              dangerouslySetInnerHTML={{ __html: whyUs?.heading }}
            />
          </h2>
          <p className="text-secondary-text-color text-2xl text-center sm:text-start">
            <span
              className="text-secondary-text-color"
              dangerouslySetInnerHTML={{ __html: whyUs?.description }}
            />
          </p>
        </div>
      )}

      {whyUs?.keypoints?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {whyUs?.keypoints?.map((feature: any, index: any) => (
            <div
              key={index}
              className="flex flex-col items-start text-left rounded-lg border border-secondary-100 bg-card-color p-6 transition-shadow hover:shadow-xl"
            >
              {isUsableUrl(feature?.image_url) && (
                <img
                  className="text-blue-500 text-4xl mb-4 max-w-14 max-h-14 rounded-md"
                  src={feature.image_url}
                  alt={feature.image_url}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
              <span
                className="leading-tight text-text-color text-2xl font-semibold mb-2"
                dangerouslySetInnerHTML={{ __html: feature.heading }}
              />
              <p
                className="text-secondary-text-color text-2xl leading-tight"
                dangerouslySetInnerHTML={{ __html: feature.description }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WhyChooseUS;
